import { useQuery } from "@tanstack/react-query";
import { Building2, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errorMessage, unwrap } from "../../api/client";
import { Alert, Badge, Card, Empty, PageHead, Spinner } from "../../components/ui";
import { date, initials } from "../../lib/format";

export function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setV(value), ms);
    return () => window.clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function AdminOrgs() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const query = useDebounced(q.trim());
  const orgs = useQuery({
    queryKey: ["admin-orgs", query],
    queryFn: () => unwrap(api.GET("/v1/admin/orgs", { params: { query: { q: query || undefined, limit: 100 } } })),
    placeholderData: (prev) => prev,
  });
  return (
    <>
      <PageHead title="Organisations" description="Every installer on SolarOS. Search by name, slug, id or a member’s email." />
      <Card pad={false}>
        <div className="toolbar">
          <div className="input-icon">
            <Search />
            <input className="input" placeholder="Search organisations or member emails" value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
          </div>
          {orgs.data && <span className="small faint">{orgs.data.items.length} shown</span>}
        </div>
        {orgs.isLoading ? (
          <Spinner />
        ) : orgs.error ? (
          <div className="card-body">
            <Alert tone="danger">{errorMessage(orgs.error)}</Alert>
          </div>
        ) : !orgs.data?.items.length ? (
          <Empty icon={<Building2 />} title="No organisations found" />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Organisation</th>
                  <th>Status</th>
                  <th className="hide-sm">Plan</th>
                  <th className="hide-sm">Country</th>
                  <th className="num">Credits</th>
                  <th className="num hide-sm">Created</th>
                </tr>
              </thead>
              <tbody>
                {orgs.data.items.map(({ organization: o, balance }) => (
                  <tr key={o.id} className="clickable" onClick={() => navigate(`/admin/orgs/${o.id}`)}>
                    <td>
                      <div className="row" style={{ flexWrap: "nowrap" }}>
                        <div className="org-avatar">{initials(o.name)}</div>
                        <div style={{ minWidth: 0 }}>
                          <div className="cell-main ellipsis">{o.name}</div>
                          <div className="cell-sub mono">{o.slug || o.id.slice(0, 8)}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <Badge tone={o.status === "active" ? "ok" : "danger"}>{o.status}</Badge>
                    </td>
                    <td className="hide-sm">{o.plan || "free"}</td>
                    <td className="hide-sm">{o.country || "—"}</td>
                    <td className="num" style={{ fontWeight: 600, color: balance <= 0 ? "var(--danger)" : undefined }}>
                      {balance}
                    </td>
                    <td className="num hide-sm">{date(o.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
